import { useState, useEffect, useCallback } from 'react';
import '../pages/DashboardPage.css';

function LoansPage() {
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedLoanType, setSelectedLoanType] = useState('');
    const [selectedLoanForPayment, setSelectedLoanForPayment] = useState(null);
    const [selectedLoanForDetails, setSelectedLoanForDetails] = useState(null);

    const [loans, setLoans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const API_URL = process.env.REACT_APP_API_URL || '';

    const fetchLoans = useCallback(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (session && session.userId) {
            setIsLoading(true);
            fetch(`${API_URL}/api/loans/${session.userId}`)
                .then(res => res.json())
                .then(data => {
                    if (data.success) {
                        // Map database fields to frontend fields - include ALL fields
                        const mappedLoans = data.loans.map(loan => ({
                            id: loan.id,
                            type: loan.type,
                            amount: loan.amount.toLocaleString('en-IN'),
                            rawAmount: loan.amount,
                            status: loan.status,
                            emi: loan.emi.toLocaleString('en-IN'),
                            rawEmi: loan.emi,
                            dueDate: new Date(loan.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
                            progress: loan.progress,
                            accountNumber: loan.account_number,
                            // Additional fields for details modal
                            kyc_status: loan.kyc_status,
                            approval_status: loan.approval_status,
                            interest_rate: loan.interest_rate,
                            tenure_months: loan.tenure_months,
                            application_date: loan.application_date,
                            reviewed_at: loan.reviewed_at,
                            reviewed_by_name: loan.reviewed_by_name,
                            rejection_reason: loan.rejection_reason,
                            disbursement_date: loan.disbursement_date,
                            created_at: loan.created_at
                        }));
                        setLoans(mappedLoans);
                    }
                })
                .catch(err => console.error('Error fetching loans:', err))
                .finally(() => setIsLoading(false));
        }
    }, [API_URL]);

    useEffect(() => {
        fetchLoans();
    }, [fetchLoans]);

    const handleApplyLoan = (loanType) => {
        setSelectedLoanType(loanType);
        setShowApplyModal(true);
    };

    const handlePayEMI = (loan) => {
        setSelectedLoanForPayment(loan);
        setShowPaymentModal(true);
    };

    const handleSubmitApplication = async (e) => {
        e.preventDefault();
        const amount = e.target.elements[1].value;
        const session = JSON.parse(localStorage.getItem('userSession'));

        try {
            const response = await fetch(`${API_URL}/api/loans/apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    type: selectedLoanType || e.target.elements[0].value,
                    amount: parseFloat(amount)
                })
            });
            const data = await response.json();

            if (data.success) {
                let alertMessage = data.message || 'Loan application submitted successfully!';

                if (data.requiresKyc) {
                    alertMessage += '\n\nPlease complete your KYC verification to proceed with the approval.';
                } else if (data.approvalStatus === 'KYC_Approved') {
                    alertMessage += '\n\n✅ Your application is now pending admin review.';
                }

                alert(alertMessage);
                setShowApplyModal(false);
                setSelectedLoanType('');
                fetchLoans();
            } else {
                alert(data.message || 'Failed to submit application. Please try again.');
            }
        } catch (err) {
            console.error('Loan application error:', err);
            alert('Failed to submit application. Please try again.');
        }
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        const session = JSON.parse(localStorage.getItem('userSession'));

        try {
            const response = await fetch(`${API_URL}/api/payments/pay`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: session.userId,
                    loanId: selectedLoanForPayment.id,
                    amount: parseFloat(selectedLoanForPayment.emi.replace(/,/g, '')),
                    method: 'UPI'
                })
            });
            const data = await response.json();
            if (data.success) {
                alert('Payment processed successfully! Your EMI has been paid.');
                setShowPaymentModal(false);
                setSelectedLoanForPayment(null);
                fetchLoans();
            }
        } catch (err) {
            alert('Payment failed. Please try again.');
        }
    };

    const handleViewDetails = (loan) => {
        setSelectedLoanForDetails(loan);
        setShowDetailsModal(true);
    };

    const closeAllModals = () => {
        setShowApplyModal(false);
        setShowPaymentModal(false);
        setShowDetailsModal(false);
        setSelectedLoanForDetails(null);
    };

    return (
        <div className="dashboard-page-new">
            {/* Page Header */}
            <div className="welcome-section">
                <div>
                    <h1>My Loans 💰</h1>
                    <p className="welcome-subtitle">View and manage all your loan applications</p>
                </div>
                <button className="btn-primary" onClick={() => handleApplyLoan('')}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Apply for New Loan
                </button>
            </div>

            {/* Active Loans Section */}
            <div className="content-section loans-section-new">
                <div className="section-header-new">
                    <h2>Your Active Loans</h2>
                    <span className="loan-count">{loans.length} active loans</span>
                </div>

                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <p>Loading your loans...</p>
                    </div>
                ) : loans.length > 0 ? (
                    <div className="loans-grid">
                        {loans.map((loan) => (
                            <div key={loan.id} className="loan-card-new">
                                <div className="loan-card-header">
                                    <div>
                                        <h3>{loan.type}</h3>
                                        <span className="account-number">{loan.accountNumber}</span>
                                    </div>
                                    <span className={`status-badge ${loan.status.toLowerCase()}`}>
                                        {loan.status}
                                    </span>
                                </div>

                                <div className="loan-amount-display">
                                    <span className="amount-label">Loan Amount</span>
                                    <span className="amount-value">₹{loan.amount}</span>
                                </div>

                                <div className="loan-info-grid">
                                    <div className="info-item">
                                        <span className="info-label">Monthly EMI</span>
                                        <span className="info-value">₹{loan.emi}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Next Due</span>
                                        <span className="info-value">{loan.dueDate}</span>
                                    </div>
                                </div>

                                <div className="loan-progress-section">
                                    <div className="progress-label">
                                        <span>Repayment Progress</span>
                                        <span className="progress-percent">{loan.progress}%</span>
                                    </div>
                                    <div className="progress-bar-new">
                                        <div className="progress-bar-fill" style={{ width: `${loan.progress}%` }}></div>
                                    </div>
                                </div>

                                <button
                                    className="btn-secondary"
                                    onClick={() => handleViewDetails(loan)}
                                    style={{ width: '100%', marginTop: '1rem' }}
                                >
                                    View Details
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <p>No active loans found. Apply for one today!</p>
                    </div>
                )}
            </div>

            {/* Apply Loan Modal */}
            {showApplyModal && (
                <div className="modal-overlay" onClick={closeAllModals}>
                    <div className="loan-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeAllModals}>×</button>
                        <h2>Apply for {selectedLoanType || 'Loan'}</h2>
                        <p className="modal-subtitle">Fill in the details below to apply</p>
                        <form onSubmit={handleSubmitApplication}>
                            <div className="form-group">
                                <label>Loan Type *</label>
                                <select required value={selectedLoanType} onChange={(e) => setSelectedLoanType(e.target.value)}>
                                    <option value="">Select loan type</option>
                                    <option value="Personal Loan">Personal Loan</option>
                                    <option value="Two-Wheeler Loan">Two-Wheeler Loan</option>
                                    <option value="Three-Wheeler Loan">Three-Wheeler Loan</option>
                                    <option value="Loan Against Property">Loan Against Property</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Loan Amount (₹) *</label>
                                <input type="number" required placeholder="Enter amount" min="10000" />
                            </div>
                            <div className="form-group">
                                <label>Purpose *</label>
                                <textarea rows="3" required placeholder="Explain the purpose of the loan"></textarea>
                            </div>
                            <div className="form-group">
                                <label>Monthly Income (₹) *</label>
                                <input type="number" required placeholder="Enter monthly income" />
                            </div>
                            <div className="form-actions">
                                <button type="button" className="btn-secondary" onClick={closeAllModals}>Cancel</button>
                                <button type="submit" className="btn-primary">Submit Application</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {showPaymentModal && selectedLoanForPayment && (
                <div className="modal-overlay" onClick={closeAllModals}>
                    <div className="loan-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeAllModals}>×</button>
                        <h2>Pay EMI - {selectedLoanForPayment.type}</h2>
                        <p className="modal-subtitle">Account: {selectedLoanForPayment.accountNumber}</p>
                        <form onSubmit={handlePaymentSubmit}>
                            <div className="payment-summary">
                                <div className="summary-item">
                                    <span>EMI Amount</span>
                                    <strong>₹{selectedLoanForPayment.emi}</strong>
                                </div>
                                <div className="summary-item">
                                    <span>Due Date</span>
                                    <strong>{selectedLoanForPayment.dueDate}</strong>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Payment Method *</label>
                                <select required>
                                    <option value="">Select payment method</option>
                                    <option value="upi">UPI</option>
                                    <option value="netbanking">Net Banking</option>
                                    <option value="debit">Debit Card</option>
                                    <option value="credit">Credit Card</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Amount (₹) *</label>
                                <input type="number" required defaultValue={selectedLoanForPayment.emi.replace(',', '')} min="1" />
                            </div>
                            <div className="form-actions">
                                <button type="button" className="btn-secondary" onClick={closeAllModals}>Cancel</button>
                                <button type="submit" className="btn-primary">Proceed to Pay</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Loan Details Modal */}
            {showDetailsModal && selectedLoanForDetails && (
                <div className="modal-overlay" onClick={closeAllModals}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>📋 Loan Details</h2>
                            <button className="close-btn" onClick={closeAllModals}>×</button>
                        </div>
                        <div className="loan-details-content" style={{ padding: '1.5rem' }}>
                            <div className="detail-section">
                                <h3 style={{ marginBottom: '1rem', color: '#1a1a1a' }}>Loan Information</h3>
                                <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Loan Type</label>
                                        <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1a1a1a' }}>{selectedLoanForDetails.type}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Account Number</label>
                                        <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1a1a1a' }}>{selectedLoanForDetails.accountNumber}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Loan Amount</label>
                                        <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1a1a1a' }}>₹{selectedLoanForDetails.amount}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Status</label>
                                        <p style={{ fontSize: '1rem', fontWeight: '600', color: selectedLoanForDetails.status === 'Active' ? '#10b981' : '#f59e0b' }}>
                                            {selectedLoanForDetails.status}
                                        </p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Monthly EMI</label>
                                        <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1a1a1a' }}>₹{selectedLoanForDetails.emi}</p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Next Due Date</label>
                                        <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1a1a1a' }}>{selectedLoanForDetails.dueDate}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Approval Status Section */}
                            <div className="detail-section" style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8fafc', borderRadius: '12px' }}>
                                <h3 style={{ marginBottom: '1rem', color: '#1a1a1a' }}>📋 Approval Status</h3>
                                <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>KYC Status</label>
                                        <p style={{
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            color: selectedLoanForDetails.kyc_status === 'completed' ? '#10b981' : '#f59e0b',
                                            textTransform: 'capitalize'
                                        }}>
                                            {selectedLoanForDetails.kyc_status === 'completed' ? '✅ Verified' :
                                                selectedLoanForDetails.kyc_status === 'pending' ? '⏳ Pending' :
                                                    selectedLoanForDetails.kyc_status === 'in_progress' ? '🔄 In Progress' :
                                                        '❌ Rejected'}
                                        </p>
                                    </div>
                                    <div className="detail-item">
                                        <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Admin Approval</label>
                                        <p style={{
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            color: selectedLoanForDetails.approval_status === 'Admin_Approved' ? '#10b981' :
                                                selectedLoanForDetails.approval_status === 'KYC_Approved' ? '#3b82f6' :
                                                    selectedLoanForDetails.approval_status === 'Rejected' ? '#ef4444' : '#f59e0b'
                                        }}>
                                            {selectedLoanForDetails.approval_status === 'Admin_Approved' ? '✅ Approved' :
                                                selectedLoanForDetails.approval_status === 'KYC_Approved' ? '🔍 Under Review' :
                                                    selectedLoanForDetails.approval_status === 'Rejected' ? '❌ Rejected' :
                                                        '⏳ Pending'}
                                        </p>
                                    </div>
                                    {selectedLoanForDetails.reviewed_by_name && (
                                        <div className="detail-item">
                                            <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Reviewed By</label>
                                            <p style={{ fontSize: '0.95rem', fontWeight: '600', color: '#1a1a1a' }}>
                                                {selectedLoanForDetails.reviewed_by_name}
                                            </p>
                                        </div>
                                    )}
                                    {selectedLoanForDetails.disbursement_date && (
                                        <div className="detail-item">
                                            <label style={{ fontSize: '0.875rem', color: '#64748b', display: 'block', marginBottom: '0.25rem' }}>Disbursement Date</label>
                                            <p style={{ fontSize: '0.95rem', fontWeight: '600', color: '#10b981' }}>
                                                {new Date(selectedLoanForDetails.disbursement_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                {selectedLoanForDetails.rejection_reason && (
                                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#fee2e2', borderRadius: '8px', borderLeft: '4px solid #ef4444' }}>
                                        <label style={{ fontSize: '0.875rem', color: '#991b1b', display: 'block', marginBottom: '0.25rem', fontWeight: '600' }}>Rejection Reason</label>
                                        <p style={{ fontSize: '0.875rem', color: '#7f1d1d', margin: 0 }}>
                                            {selectedLoanForDetails.rejection_reason}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="detail-section" style={{ marginTop: '1.5rem' }}>
                                <h3 style={{ marginBottom: '1rem', color: '#1a1a1a' }}>Repayment Progress</h3>
                                <div className="progress-info" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Completed</span>
                                    <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1a1a1a' }}>{selectedLoanForDetails.progress}%</span>
                                </div>
                                <div className="progress-bar" style={{
                                    width: '100%',
                                    height: '12px',
                                    backgroundColor: '#f1f5f9',
                                    borderRadius: '6px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        width: `${selectedLoanForDetails.progress}%`,
                                        height: '100%',
                                        background: 'linear-gradient(135deg, #f7bd20, #ffd700)',
                                        transition: 'width 0.3s ease'
                                    }}></div>
                                </div>
                            </div>

                            <div className="form-actions" style={{ marginTop: '2rem' }}>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={closeAllModals}
                                    style={{ width: '100%' }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LoansPage;
